<template>
  <div class="relative">
    <div v-if="customizable && files.length > 1" class="relative">
      <div class="relative rounded-md overflow-hidden">
        <img
          v-if="currentFileUrl && isFileImage(currentFileUrl)"
          id="customization-preview-image"
          :src="currentFileUrl"
          class="w-full h-full object-contain rounded-md"
          alt="Customizable Image"
          @load="emit('load', $event.target)"
        />
        <video
          v-else-if="currentFileUrl && isFileVideo(currentFileUrl)"
          :src="currentFileUrl"
          class="w-full h-full rounded-md bg-black"
          controls
        />

        <div v-show="showQuadrantsOverlay" class="absolute inset-0">
          <QuadrantsOverlay
            ref="quadrantsOverlay"
            :quadrants="currentFileQuadrants"
            :original-dimensions="props.originalDimensions"
            :img-id="getImageId()"
            :logo="currentPageLogo"
            :preview="preview"
            @update:image-data="
              (value, pageIndex) => emit('update:image-data', value, pageIndex)
            "
            @update:logo="emit('update:logo', $event)"
          />
        </div>
      </div>

      <div
        class="mt-4 flex gap-2 justify-start overflow-x-auto max-w-full pb-2"
      >
        <button
          v-for="(file, index) in files"
          :key="`thumb-${file.id}`"
          :class="getFileButtonClasses(index)"
          class="relative w-16 h-16 rounded-sm border-2 transition-all duration-200 overflow-hidden flex-shrink-0"
          type="button"
          @click.prevent.stop="selectFile(index)"
        >
          <img
            v-if="isFileImage(file.src)"
            :src="file.src"
            class="w-full h-full object-cover"
            :alt="`File ${index + 1}`"
          />
          <video
            v-else-if="isFileVideo(file.src)"
            :src="file.src"
            class="w-full h-full object-cover"
            muted
          />

          <div
            v-if="selectedIndex === index"
            class="absolute inset-0 bg-blue-500 bg-opacity-20 flex items-center justify-center"
          >
            <div
              class="bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold"
            >
              {{ index + 1 }}
            </div>
          </div>

          <div
            v-if="getFileQuadrantCount(index) > 0"
            class="absolute bottom-0 right-0 bg-green-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold"
          >
            {{ getFileQuadrantCount(index) }}
          </div>
        </button>
      </div>
    </div>

    <Carousel
      v-else
      :variant="variant"
      :files="files"
      @load="emit('load', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue';
import Carousel from './Carousel.vue';
import { CarouselVariant } from './carousel.constants';
import { isFileImage, isFileVideo } from '~/utils/file';
import type { Box } from '~/composables/use-quadrants';

const QuadrantsOverlay = defineAsyncComponent(
  () =>
    import('~/pages/content/my-posts/_components/quadrants-overlay/quadrants-overlay.vue'),
);

type Props = {
  variant?: CarouselVariant;
  files: { src: string; id: string }[];
  selectedIndex: number;
  customizable: boolean;
  quadrants?: Box[];
  originalDimensions?:
    | { width: number; height: number; type: string }
    | Record<number, { width: number; height: number; type: string }>
    | null;
  logo?: any;
  logos?: any[];
  preview?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  variant: CarouselVariant.GRID,
  quadrants: () => [],
  preview: false,
});

const emit = defineEmits<{
  load: [target?: EventTarget | null];
  'update:selected-index': [index: number];
  'update:image-data': [value: string, pageIndex: number];
  'update:logo': [value: any];
}>();

const quadrantsOverlay = ref<any>(null);

const getImageId = () => {
  return 'customization-preview-image';
};

const currentFileUrl = computed(() => {
  return props.files[props.selectedIndex]?.src || '';
});

const selectFile = (index: number) => {
  emit('update:selected-index', index);
  nextTick(() => {
    if (quadrantsOverlay.value && 'rerenderStage' in quadrantsOverlay.value) {
      quadrantsOverlay.value.rerenderStage(getImageId());
    }
  });
};

const getFileQuadrantCount = (fileIndex: number): number => {
  if (!props.quadrants) return 0;

  const targetPage = fileIndex + 1;
  return props.quadrants.filter((q) => (q.page || 1) === targetPage).length;
};

const getFileButtonClasses = (index: number) => {
  const isSelected = props.selectedIndex === index;
  const hasQuadrants = getFileQuadrantCount(index) > 0;

  return [
    'cursor-pointer',
    isSelected
      ? 'border-blue-500 ring-2 ring-blue-200'
      : 'border-gray-300 hover:border-blue-300',
    hasQuadrants ? 'ring-1 ring-green-300' : '',
  ];
};

const currentFileQuadrants = computed(() => {
  if (!props.quadrants || !props.customizable) {
    return [];
  }

  const targetPage = props.selectedIndex + 1;

  const filtered = props.quadrants.filter((q) => (q.page || 1) === targetPage);

  return filtered;
});

const currentPageLogo = computed(() => {
  if (props.logos && props.logos.length > 0) {
    const targetPage = props.selectedIndex + 1;
    const found = props.logos.find((logo) => (logo.page || 1) === targetPage);
    return found;
  }
  return props.logo;
});

const showQuadrantsOverlay = computed(() => {
  const shouldShow =
    props.customizable &&
    currentFileQuadrants.value.length > 0 &&
    currentFileUrl.value &&
    isFileImage(currentFileUrl.value);

  return shouldShow;
});

const renderAllPages = async (): Promise<void> => {
  const totalPages = props.files.length;
  const originalIndex = props.selectedIndex;

  /* eslint-disable no-await-in-loop */
  for (let i = 0; i < totalPages; i++) {
    if (getFileQuadrantCount(i) === 0) {
      continue;
    }

    emit('update:selected-index', i);

    await nextTick();

    await new Promise((resolve) => {
      setTimeout(() => resolve(), 300);
    });

    if (quadrantsOverlay.value && 'rerenderStage' in quadrantsOverlay.value) {
      quadrantsOverlay.value.rerenderStage(getImageId());
    }

    if (
      quadrantsOverlay.value &&
      'saveAllLogosForCurrentPage' in quadrantsOverlay.value
    ) {
      quadrantsOverlay.value.saveAllLogosForCurrentPage();
    }

    await new Promise((resolve) => {
      setTimeout(() => resolve(), 100);
    });
  }
  /* eslint-enable no-await-in-loop */

  emit('update:selected-index', originalIndex);
  await nextTick();
};

defineExpose({
  rerenderStage: () => {
    if (quadrantsOverlay.value && 'rerenderStage' in quadrantsOverlay.value) {
      quadrantsOverlay.value.rerenderStage(getImageId());
    }
  },
  getAllLogos: (accountLogoId: number | string) => {
    if (quadrantsOverlay.value && 'getAllLogos' in quadrantsOverlay.value) {
      return quadrantsOverlay.value.getAllLogos(accountLogoId);
    }
    return [];
  },
  renderAllPages,
});
</script>

<style scoped>
.cursor-pointer:hover {
  transform: scale(1.05);
}
</style>
